import Dexie, { type EntityTable } from 'dexie';
import type { ArquivoLocal, ArquivosStore } from './arquivos.js';
import { chavePrimaria } from './ids.js';
import { estadoInicial, type ItemEmQuarentena, type LocalStore, type OutboxItem, type RemoteChange, type SyncState } from './types.js';

/**
 * Armazenamento local no dispositivo, sobre IndexedDB.
 *
 * IndexedDB e nao OPFS/WASM de proposito: e o caminho mais testado e mais
 * previsivel no iOS Safari, que e onde um PWA perde dado quando alguem erra.
 *
 * O contrato e o mesmo do MemoryStore, que e a implementacao de referencia
 * testada sob falha. Se as duas divergirem, a que esta errada e esta.
 */

/** Entidades replicadas para o dispositivo. Espelha syncable_entity no Postgres. */
export const ENTITIES = [
  'stud_farm',
  'person',
  'animal',
  'instrument',
  'visit',
  'assessment',
  'measurement_session',
  'measurement',
  'animal_management',
  'media',
  'gait_trial',
  'gait_subjective',
  'sensor_pack',
  'validation_override',
  'media_analysis',
  'amendment',
  'farm_access',
  'marker_check',
] as const;

export type EntityName = (typeof ENTITIES)[number];

interface QuarantineRow {
  id?: number;
  item: OutboxItem;
  reason: string;
  at: number;
}

interface MetaRow {
  key: string;
  value: unknown;
}

export class EquiviewDb extends Dexie {
  outbox!: EntityTable<OutboxItem, 'clientMutationId'>;
  quarantine!: EntityTable<QuarantineRow, 'id'>;
  meta!: EntityTable<MetaRow, 'key'>;
  arquivo!: EntityTable<ArquivoLocal, 'mediaId'>;

  /**
   * O nome do banco foi trocado de 'compasso' para 'equiview' em 2026-09-23,
   * junto com o rename do produto. Foi de graca porque nenhum dado de campo
   * existia ainda. A partir daqui nao e mais: trocar este nome cria um banco
   * novo e vazio no dispositivo, e o que estiver no antigo fica inalcancavel.
   * Qualquer mudanca futura aqui exige migracao explicita.
   */
  constructor(name = 'equiview') {
    super(name);

    // animal_management e gait_subjective sao 1-para-1 com a ficha: a chave e
    // assessment_id, igual ao Postgres.
    const entitySchemas: Record<string, string> = {
      stud_farm: 'id, name',
      person: 'id, full_name',
      animal: 'id, owner_farm_id, abccmm_registry, name',
      instrument: 'id, kind',
      visit: 'id, stud_farm_id, visit_date',
      assessment: 'id, visit_id, animal_id, status',
      measurement_session: 'id, assessment_id',
      measurement: 'id, session_id, trait_code_id',
      animal_management: 'assessment_id',
      media: 'id, assessment_id',
      gait_trial: 'id, assessment_id',
      gait_subjective: 'assessment_id',
      sensor_pack: 'id, assessment_id',
      validation_override: 'id, assessment_id',
    };

    this.version(1).stores({
      outbox: 'clientMutationId, createdAt, entity',
      quarantine: '++id, at',
      meta: 'key',
      ...entitySchemas,
    });

    // v2 (2026-09-27): analise de foto/video. So acrescenta uma tabela; a v1
    // fica intacta para o Dexie migrar sem tocar no que ja esta no aparelho.
    this.version(2).stores({
      media_analysis: 'id, media_id',
    });

    // v3 (2026-09-27): fotos e videos esperando para subir ao Storage. Fora de
    // ENTITIES de proposito: o binario nunca passa pelo outbox do sync.
    this.version(3).stores({
      arquivo: 'mediaId, estado, criadoEm',
    });

    // v4 (2026-09-27): emendas de ficha fechada, com o historico no aparelho.
    this.version(4).stores({
      amendment: 'id, entity_id',
    });

    // v5 (2026-09-28): acesso de cada dono ao seu haras, gerido pelo admin no app.
    this.version(5).stores({
      farm_access: 'id, person_id, stud_farm_id',
    });

    // v6 (2026-09-28): checklist dos marcadores M1-M18 da Estacao 2.
    this.version(6).stores({
      marker_check: 'id, assessment_id',
    });
  }

  table_(entity: string) {
    return this.table(entity);
  }
}

export class DexieStore implements LocalStore {
  constructor(
    private readonly db: EquiviewDb,
    private readonly device: string,
  ) {}

  async enqueue(item: OutboxItem): Promise<void> {
    await this.db.outbox.put(item);
  }

  async gravarLocal(item: OutboxItem): Promise<void> {
    if (!(ENTITIES as readonly string[]).includes(item.entity)) {
      throw new Error(`entidade ${item.entity} nao existe no banco local`);
    }
    const table = this.db.table_(item.entity);
    const pk = chavePrimaria(item.entity);
    const agora = new Date(item.createdAt).toISOString();
    // Linha e fila na mesma transacao: ou as duas gravam, ou nenhuma.
    await this.db.transaction('rw', [table, this.db.outbox], async () => {
      const atual = (await table.get(item.entityId)) ?? undefined;
      await table.put(
        item.op === 'delete'
          ? { ...atual, [pk]: item.entityId, deleted_at: agora }
          : { created_at: agora, ...atual, ...item.payload, [pk]: item.entityId },
      );
      await this.db.outbox.put(item);
    });
  }

  async gravarSoLocal(entity: string, entityId: string, patch: Record<string, unknown>): Promise<void> {
    if (!(ENTITIES as readonly string[]).includes(entity)) return;
    const table = this.db.table_(entity);
    const atual = (await table.get(entityId)) ?? {};
    await table.put({ ...atual, ...patch, [chavePrimaria(entity)]: entityId });
  }

  async peekOutbox(limit: number): Promise<OutboxItem[]> {
    // Ordem de criacao: a ficha e escrita na ordem das estacoes, e as chaves
    // estrangeiras dependem disso (visita antes da ficha, ficha antes da medida).
    return this.db.outbox.orderBy('createdAt').limit(limit).toArray();
  }

  async dropFromOutbox(ids: string[]): Promise<void> {
    await this.db.outbox.bulkDelete(ids);
  }

  async recordFailure(clientMutationId: string, error: string): Promise<void> {
    await this.db.outbox
      .where('clientMutationId')
      .equals(clientMutationId)
      .modify((item) => {
        item.attempts += 1;
        item.lastError = error;
      });
  }

  async quarantine(item: OutboxItem, reason: string): Promise<void> {
    await this.db.quarantine.add({ item, reason, at: Date.now() });
  }

  async listQuarantine(): Promise<ItemEmQuarentena[]> {
    const linhas = await this.db.quarantine.toArray();
    return linhas
      .map((q) => ({ id: q.id!, item: q.item, reason: q.reason, at: q.at }))
      .sort((a, b) => a.item.createdAt - b.item.createdAt);
  }

  async requeueFromQuarantine(id: number, ajuste?: Record<string, unknown>): Promise<void> {
    await this.db.transaction('rw', [this.db.quarantine, this.db.outbox], async () => {
      const q = await this.db.quarantine.get(id);
      if (!q) return;
      await this.db.outbox.put({ ...q.item, payload: { ...q.item.payload, ...ajuste }, attempts: 0, lastError: null });
      await this.db.quarantine.delete(id);
    });
  }

  async discardFromQuarantine(id: number): Promise<void> {
    const q = await this.db.quarantine.get(id);
    if (!q) return;
    const conhecida = (ENTITIES as readonly string[]).includes(q.item.entity);
    const tabelas = conhecida ? [this.db.table_(q.item.entity)] : [];
    await this.db.transaction('rw', [this.db.quarantine, this.db.outbox, ...tabelas], async () => {
      await this.db.quarantine.delete(id);
      if (!conhecida) return;
      const pendente = await this.db.outbox
        .where('entity')
        .equals(q.item.entity)
        .filter((i) => i.entityId === q.item.entityId)
        .count();
      if (pendente === 0) await this.db.table_(q.item.entity).delete(q.item.entityId);
    });
  }

  async applyRemote(changes: RemoteChange[]): Promise<void> {
    const conhecidas = new Set<string>(ENTITIES);
    const tabelas = [...new Set(changes.map((c) => c.entity))].filter((e) => conhecidas.has(e));

    await this.db.transaction('rw', [...tabelas.map((t) => this.db.table_(t)), this.db.outbox], async () => {
      for (const c of changes) {
        if (!conhecidas.has(c.entity)) continue; // versao do servidor mais nova que a do app
        const pendente = await this.db.outbox
          .where('entity')
          .equals(c.entity)
          .filter((i) => i.entityId === c.entity_id)
          .count();
        if (pendente > 0) continue; // edicao local ainda nao subiu: ela manda
        const table = this.db.table_(c.entity);
        if (c.op === 'delete') {
          const atual = (await table.get(c.entity_id)) ?? {};
          await table.put({ ...atual, ...this.keyOf(c), deleted_at: c.created_at });
        } else {
          const atual = (await table.get(c.entity_id)) ?? {};
          await table.put({ ...atual, ...c.payload });
        }
      }
    });
  }

  private keyOf(c: RemoteChange): Record<string, string> {
    return { [chavePrimaria(c.entity)]: c.entity_id };
  }

  async getSyncState(): Promise<SyncState> {
    const row = await this.db.meta.get('syncState');
    // Estado gravado por uma versao anterior do app nao tem os campos novos.
    return { ...estadoInicial(this.device), ...(row?.value as Partial<SyncState> | undefined) };
  }

  async setSyncState(state: SyncState): Promise<void> {
    await this.db.meta.put({ key: 'syncState', value: state });
  }

  async pendingCount(): Promise<number> {
    return this.db.outbox.count();
  }
}

/** Arquivos esperando envio, no IndexedDB. O contrato e o do MemoryArquivos. */
export class DexieArquivos implements ArquivosStore {
  constructor(private readonly db: EquiviewDb) {}

  async guardar(a: ArquivoLocal): Promise<void> {
    await this.db.arquivo.put(a);
  }

  async pendentes(): Promise<ArquivoLocal[]> {
    return this.db.arquivo.orderBy('criadoEm').toArray();
  }

  async obter(mediaId: string): Promise<ArquivoLocal | undefined> {
    return this.db.arquivo.get(mediaId);
  }

  async marcarErro(mediaId: string, erro: string): Promise<void> {
    await this.db.arquivo.update(mediaId, { estado: 'erro', erro });
  }

  async apagar(mediaId: string): Promise<void> {
    await this.db.arquivo.delete(mediaId);
  }
}

/**
 * Pede ao navegador que nao descarte o armazenamento.
 *
 * Sem isso, no iOS, um PWA que nao foi instalado na tela de inicio perde o
 * IndexedDB depois de alguns dias sem uso — o que, num tablet que passa quatro
 * dias no haras, significa perder o dia de coleta. Chamar cedo, no onboarding.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    if (!navigator.storage?.persist) return false;
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}
